/**
 * Native asset registry — prefab structures + interior builders, plus the full
 * asset definition list consumed by the World Builder. All builders share the
 * nativeKit Norse visual language. Each definition: { id, name, sub, tier,
 * sourceType:'native', collision }. collision: 'cuboid' | 'none'.
 */

import * as THREE from 'three';
import { NATIVE_BUILDERS, G, BOX, CYL, CONE, SPH, M, mat, emat, C, rnd, MODULE, WALL_H, runeRow, dragonHead, carvedPost } from './nativeKit';

// --------- shared structure helpers (reuse the modular visual language) -----
function timberFrame(w, d, h, color) {
  const g = new THREE.Group();
  const hw = w / 2, hd = d / 2;
  [[-hw, -hd], [hw, -hd], [-hw, hd], [hw, hd]].forEach(([x, z]) => g.add(BOX(0.28, h, 0.28, C.logDark, x, h / 2, z)));
  g.add(BOX(w, h - 1, d, color, 0, (h - 1) / 2 + 0.5, 0, 0, 0.92));
  g.add(BOX(w + 0.3, 1, d + 0.3, C.stone, 0, 0.5, 0, 0, 1)); // field-stone base
  return g;
}
function gableTurfRoof(w, d, baseY, color = C.turf, steep = false, dragons = false) {
  const g = new THREE.Group();
  const rise = steep ? w * 0.8 : w * 0.5;
  const slope = Math.sqrt((w / 2) ** 2 + rise ** 2) + 0.4;
  const pitch = Math.atan2(rise, w / 2);
  const s1 = BOX(slope, 0.22, d + 0.6, color, -w / 4, baseY + rise / 2, 0); s1.rotation.z = pitch; g.add(s1);
  const s2 = BOX(slope, 0.22, d + 0.6, color, w / 4, baseY + rise / 2, 0); s2.rotation.z = -pitch; g.add(s2);
  g.add(BOX(0.25, 0.25, d + 0.6, C.timberDark, 0, baseY + rise, 0));
  if (color === C.turf) for (let i = 0; i < 14; i++) g.add(M(new THREE.ConeGeometry(0.05, 0.22, 4), mat(C.grassA), rnd(-w / 2, w / 2), baseY + rnd(0.2, rise), rnd(-d / 2, d / 2)));
  if (dragons) [-(d / 2 + 0.3), d / 2 + 0.3].forEach((z) => { const dh = dragonHead(1.2, C.bronze); dh.position.set(0, baseY + rise + 0.1, z); dh.rotation.y = z > 0 ? -Math.PI / 2 : Math.PI / 2; dh.rotation.z = 0.5; g.add(dh); });
  return g;
}
const door = (z, w = 0.9, h = 1.9) => BOX(w, h, 0.14, C.logDark, 0, h / 2 + 0.5, z);
const win = (x, z, y = 1.6) => M(new THREE.BoxGeometry(0.7, 0.7, 0.12), emat(C.glow, 0.5), x, y, z);

// --------- prefab structures (RUSTIC unless noted) -----
function hut() { const g = timberFrame(3, 3, 2.4, C.log); g.add(door(1.55)); g.add(gableTurfRoof(3.4, 3.4, 2.4)); return g; }
function logCottage() {
  // small Midgard mythic dwelling: god-posts flank the door, glowing rune lintel, dragon-prow turf roof
  const g = timberFrame(4, 4.5, 2.6, C.log);
  g.add(door(2.3));
  g.add(win(-1.3, 2.3)); g.add(win(1.3, 2.3));
  const p1 = carvedPost(2.9, C.logDark); p1.position.set(-0.75, 0, 2.35); g.add(p1);
  const p2 = carvedPost(2.9, C.logDark); p2.position.set(0.75, 0, 2.35); g.add(p2);
  g.add(BOX(1.6, 0.28, 0.18, C.beam, 0, 2.55, 2.3));
  const rr = runeRow(4, 1.1, 2.55, 2.4, 0.2); g.add(rr);
  g.add(gableTurfRoof(4.5, 5, 2.6, C.turf, false, true));
  return g;
}
function logHomeMedium() { const g = timberFrame(5, 6, 2.8, C.log); g.add(door(3.05)); g.add(win(-1.6, 3.05)); g.add(win(1.6, 3.05)); g.add(win(2.55, 0, 1.6)); g.add(gableTurfRoof(5.6, 6.6, 2.8)); return g; }
function longhouse() {
  // larger Midgard mythic hall: steep dragon-prow roof, god-post totems, glowing rune circle, corner runestones
  const g = timberFrame(5, 11, 3.2, C.timberDark);
  g.add(door(5.55, 1.4, 2.4));
  [-3.5, 0, 3.5].forEach((z) => { g.add(win(2.55, z)); g.add(win(-2.55, z)); });
  const pL = carvedPost(3.6, C.logDark); pL.position.set(-1.1, 0, 5.6); g.add(pL);
  const pR = carvedPost(3.6, C.logDark); pR.position.set(1.1, 0, 5.6); g.add(pR);
  g.add(M(new THREE.TorusGeometry(0.7, 0.07, 6, 24), emat(C.rune, 0.7), 0, 3.4, 5.55));
  [[-2.4, 5.4], [2.4, 5.4], [-2.4, -5.4], [2.4, -5.4]].forEach(([x, z]) => g.add(BOX(0.5, 1.8, 0.35, C.stoneDark, x, 0.9, z)));
  g.add(gableTurfRoof(5.6, 11.6, 3.2, C.turf, true, true));
  return g;
}
function storageShed() { const g = timberFrame(3.5, 3, 2.2, C.log); g.add(door(1.55, 1.3)); g.add(gableTurfRoof(4, 3.4, 2.2, C.timber)); return g; }
function workshop() { const g = timberFrame(4.5, 4, 2.6, C.timber); g.add(door(2.05, 1.4)); g.add(win(1.6, 2.05)); g.add(gableTurfRoof(5, 4.5, 2.6, C.timber)); g.add(CYL(0.3, 0.35, 1.5, C.stoneDark, 1.5, 3.8, -1)); return g; }
function hunterCabin() { const g = timberFrame(3.5, 4, 2.4, C.log); g.add(door(2.05)); g.add(win(1.3, 2.05)); g.add(gableTurfRoof(4, 4.5, 2.4)); g.add(BOX(0.2, 2.2, 0.2, C.logDark, 1.5, 1.1, 2.2)); return g; }
function blacksmith() {
  const g = timberFrame(4.5, 4, 2.4, C.timberDark);
  g.add(BOX(4.5, 0.2, 2, C.timberDark, 0, 2.5, 1.3)); // open front lean roof
  g.add(CYL(0.4, 0.5, 2.2, C.stoneDark, -1.5, 1.1, -1)); // forge chimney
  g.add(M(new THREE.ConeGeometry(0.3, 0.6, 8), emat(C.fire, 0.8), -1.5, 0.9, 0.6));
  g.add(gableTurfRoof(5, 4.5, 2.4, C.slate || C.stoneDark));
  return g;
}
function stable() { const g = timberFrame(4, 6, 2.4, C.log); for (let z = -1.8; z <= 1.8; z += 1.8) g.add(BOX(0.15, 1.6, 0.15, C.logDark, 2, 1.3, z)); g.add(gableTurfRoof(4.6, 6.6, 2.4, C.timber)); return g; }
function marketStall() {
  const g = new THREE.Group();
  g.add(BOX(2.4, 0.9, 1.4, C.log, 0, 0.9, 0)); // counter
  [-1.1, 1.1].forEach((x) => [-0.6, 0.6].forEach((z) => g.add(BOX(0.12, 2.2, 0.12, C.logDark, x, 1.1, z))));
  const canopy = BOX(2.8, 0.15, 1.8, C.flowerR, 0, 2.2, 0); g.add(canopy);
  for (let i = 0; i < 6; i++) g.add(BOX(0.4, 0.16, 1.8, i % 2 ? C.pale : C.flowerR, -1.2 + i * 0.47, 2.25, 0));
  return g;
}
function marketShelter() {
  const g = new THREE.Group();
  [-2, 2].forEach((x) => [-1.4, 1.4].forEach((z) => g.add(BOX(0.22, 2.6, 0.22, C.logDark, x, 1.3, z))));
  g.add(gableTurfRoof(4.6, 3.4, 2.6, C.timber));
  g.add(BOX(3.5, 0.9, 1, C.log, 0, 0.9, 0));
  return g;
}
function guardHut() { const g = timberFrame(2.5, 2.5, 2.6, C.timberDark); g.add(door(1.3)); g.add(win(1.3, 0, 1.8)); g.add(gableTurfRoof(3, 3, 2.6, C.stoneDark)); return g; }
function watchtower() {
  const g = new THREE.Group();
  g.add(BOX(2.6, 1, 2.6, C.stone, 0, 0.5, 0, 0, 1));
  g.add(timberFrame(2.2, 2.2, 5, C.timberDark));
  g.add(BOX(3, 0.3, 3, C.timberDark, 0, 5.2, 0)); // platform
  for (let i = 0; i < 4; i++) g.add(BOX(0.15, 0.8, 0.15, C.logDark, [-1.3, 1.3, 0, 0][i], 5.6, [0, 0, -1.3, 1.3][i]));
  g.add(gableTurfRoof(3.2, 3.2, 5.6, C.timber, true));
  return g;
}
function communalHouse() { const g = timberFrame(6, 8, 3.2, C.timberDark); g.add(door(4.05, 1.3, 2.3)); [-2, 2].forEach((z) => { g.add(win(3.05, z)); g.add(win(-3.05, z)); }); g.add(gableTurfRoof(6.6, 8.6, 3.2, C.turf, true, true)); return g; }
// WEALTHY
function carvedTimberHome() {
  const g = timberFrame(5, 6, 3, C.timber);
  g.add(door(3.05, 1.1, 2.1)); g.add(win(-1.6, 3.05)); g.add(win(1.6, 3.05));
  [-2.5, 2.5].forEach((x) => g.add(BOX(0.3, 3, 0.3, C.bronze, x, 1.5, 3, 0, 0.4)));
  g.add(gableTurfRoof(5.6, 6.6, 3, C.timberDark, true, true));
  return g;
}
function wealthyHouse() {
  const g = timberFrame(6, 7, 3.4, C.timber);
  g.add(BOX(6.4, 1.2, 7.4, C.stoneDress, 0, 0.6, 0, 0, 0.8));
  g.add(door(3.55, 1.2, 2.3)); [-2, 2].forEach((z) => { g.add(win(3.05, z, 2)); g.add(win(-3.05, z, 2)); });
  g.add(gableTurfRoof(6.8, 7.8, 3.4, C.stoneDark, true, true));
  return g;
}
// ASGARD
function chieftainHall() {
  const g = new THREE.Group();
  g.add(BOX(7, 1.4, 12, C.pale, 0, 0.7, 0, 0, 0.6)); // pale stone platform
  const body = timberFrame(6, 10, 4, C.timber); body.position.y = 0.6; g.add(body);
  [-3, 3].forEach((x) => g.add(BOX(0.4, 4.5, 0.4, C.gold, x, 2.8, 5, 0, 0.3)));
  g.add(door(5.6, 1.4, 2.6));
  g.add(gableTurfRoof(7, 11, 4.6, C.pale, true, true));
  // gold ridge finials
  [-5, 5].forEach((z) => g.add(M(new THREE.ConeGeometry(0.2, 1.2, 6), mat(C.gold, 0.4), 0, 9, z)));
  return g;
}

// --------- interior ----------
const bedSimple = () => G(BOX(1.2, 0.45, 2.1, C.log, 0, 0.32, 0), BOX(1.2, 0.28, 2, C.hide, 0, 0.6, 0.05), BOX(1.2, 0.3, 0.5, C.pale, 0, 0.62, -0.75), BOX(1.25, 0.9, 0.18, C.timberDark, 0, 0.75, -1.0), runeRow(3, 0.8, 1.0, -0.9, 0.18));
const bedBetter = () => G(BOX(1.3, 0.5, 2.1, C.timber, 0, 0.35, 0), BOX(1.3, 0.3, 2.1, C.flowerR, 0, 0.65, 0), BOX(0.15, 1, 0.15, C.logDark, -0.55, 0.9, -1), BOX(0.15, 1, 0.15, C.logDark, 0.55, 0.9, -1));
const bench = () => G(BOX(1.7, 0.14, 0.42, C.timber, 0, 0.46, 0), BOX(0.16, 0.46, 0.44, C.logDark, -0.76, 0.23, 0), BOX(0.16, 0.46, 0.44, C.logDark, 0.76, 0.23, 0), M(new THREE.BoxGeometry(0.1, 0.1, 0.02), emat(C.runeGlow, 0.7), -0.76, 0.3, 0.23), M(new THREE.BoxGeometry(0.1, 0.1, 0.02), emat(C.runeGlow, 0.7), 0.76, 0.3, 0.23));
const stool = () => G(CYL(0.22, 0.24, 0.12, C.log, 0, 0.5, 0), CYL(0.04, 0.04, 0.5, C.logDark, 0, 0.25, 0));
const chair = () => G(BOX(0.5, 0.1, 0.5, C.log, 0, 0.48, 0), BOX(0.5, 0.6, 0.1, C.log, 0, 0.78, -0.2), ...[[-0.2, -0.2], [0.2, -0.2], [-0.2, 0.2], [0.2, 0.2]].map(([x, z]) => BOX(0.08, 0.48, 0.08, C.logDark, x, 0.24, z)));
const table = () => G(BOX(1.8, 0.16, 1.05, C.timber, 0, 0.92, 0), BOX(1.85, 0.06, 1.1, C.beam, 0, 0.84, 0), BOX(0.2, 0.75, 0.9, C.logDark, -0.75, 0.42, 0), BOX(0.2, 0.75, 0.9, C.logDark, 0.75, 0.42, 0), BOX(1.5, 0.12, 0.14, C.logDark, 0, 0.3, 0), runeRow(5, 1.4, 0.98, 0.52, 0.14));
const longTable = () => G(BOX(3.2, 0.16, 1.1, C.timber, 0, 0.9, 0), ...[-1.4, 0, 1.4].flatMap((x) => [[x, -0.45], [x, 0.45]].map(([xx, z]) => BOX(0.16, 0.9, 0.16, C.logDark, xx, 0.45, z))));
const sideTable = () => G(BOX(0.7, 0.12, 0.7, C.timber, 0, 0.7, 0), ...[[-0.28, -0.28], [0.28, -0.28], [-0.28, 0.28], [0.28, 0.28]].map(([x, z]) => BOX(0.1, 0.7, 0.1, C.logDark, x, 0.35, z)));
const settle = () => G(BOX(1.8, 0.4, 0.7, C.timber, 0, 0.35, 0), BOX(1.8, 0.7, 0.2, C.timber, 0, 0.75, -0.25), BOX(1.8, 0.2, 0.7, C.hide, 0, 0.6, 0));
const chest = () => G(BOX(1, 0.6, 0.6, C.log, 0, 0.3, 0), M(new THREE.CylinderGeometry(0.3, 0.3, 1, 12, 1, false, 0, Math.PI), mat(C.logLight), 0, 0.6, 0, 0, 0, 0, Math.PI / 2), BOX(1.02, 0.1, 0.62, C.iron, 0, 0.55, 0));
const shelf = () => G(BOX(1, 0.1, 0.35, C.log, 0, 0.6, 0), BOX(1, 0.1, 0.35, C.log, 0, 1.1, 0), BOX(1, 0.1, 0.35, C.log, 0, 1.6, 0), BOX(0.1, 1.2, 0.35, C.logDark, -0.5, 1.1, 0), BOX(0.1, 1.2, 0.35, C.logDark, 0.5, 1.1, 0));
const candle = () => G(CYL(0.05, 0.06, 0.25, C.pale, 0, 0.12, 0), M(new THREE.ConeGeometry(0.04, 0.12, 6), emat(C.fire, 1), 0, 0.3, 0));
const candleCluster = () => G(...[[-0.15, 0], [0.15, 0], [0, 0.15]].map(([x, z]) => candle().translateX(x).translateZ(z)));
const lantern = () => G(CYL(0.12, 0.14, 0.1, C.iron, 0, 0.05, 0), M(new THREE.BoxGeometry(0.2, 0.3, 0.2), emat(C.glow, 0.8), 0, 0.26, 0), BOX(0.22, 0.05, 0.22, C.iron, 0, 0.43, 0));
function hangingLantern() { const g = lantern(); g.position.y = 0.6; const chain = CYL(0.02, 0.02, 0.6, C.iron, 0, 1.1, 0); return G(chain, g); }
const wallTorch = () => G(BOX(0.12, 0.3, 0.12, C.iron, 0, 0.15, 0), CYL(0.03, 0.03, 0.5, C.logDark, 0, 0.5, 0.1, 6), M(new THREE.ConeGeometry(0.12, 0.3, 6), emat(C.fire, 1), 0, 0.85, 0.1));
const standingTorch = () => G(CYL(0.05, 0.08, 1.6, C.logDark, 0, 0.8, 0), M(new THREE.ConeGeometry(0.15, 0.35, 6), emat(C.fire, 1), 0, 1.75, 0));
function hearth() {
  // sacred rune-brazier — iron bowl on carved legs, glowing rune ring, mythic fire
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(0.5, 0.3, 0.35, 12), mat(C.iron, 0.7, { metalness: 0.5 }), 0, 0.75, 0));
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; g.add(CYL(0.05, 0.07, 0.75, C.iron, Math.cos(a) * 0.28, 0.37, Math.sin(a) * 0.28)); }
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.add(M(new THREE.BoxGeometry(0.05, 0.12, 0.03), emat(C.runeGlow, 0.9), Math.cos(a) * 0.5, 0.78, Math.sin(a) * 0.5, a)); }
  g.add(M(new THREE.ConeGeometry(0.3, 0.7, 8), emat(C.fire, 1), 0, 1.15, 0));
  g.add(M(new THREE.ConeGeometry(0.16, 0.45, 8), emat(C.glow, 1), 0, 1.3, 0));
  return g;
}
const barrel = () => G(M(new THREE.CylinderGeometry(0.4, 0.35, 0.9, 12), mat(C.log, 0.9), 0, 0.45, 0), M(new THREE.CylinderGeometry(0.42, 0.42, 0.1, 12), mat(C.iron), 0, 0.7, 0), M(new THREE.CylinderGeometry(0.42, 0.42, 0.1, 12), mat(C.iron), 0, 0.2, 0));
const crate = () => G(BOX(0.7, 0.7, 0.7, C.log, 0, 0.35, 0), BOX(0.74, 0.1, 0.1, C.logDark, 0, 0.55, 0.33), BOX(0.74, 0.1, 0.1, C.logDark, 0, 0.15, 0.33));
const pottery = () => G(M(new THREE.SphereGeometry(0.22, 10, 8), mat(C.hide, 0.9), 0, 0.22, 0), CYL(0.1, 0.14, 0.1, C.hide, 0, 0.45, 0));
const drinkingHorn = () => G(M(new THREE.ConeGeometry(0.08, 0.5, 8), mat(C.pale, 0.7), 0, 0.1, 0, 0, 0, 0, 1.2));
const woodenBowl = () => G(M(new THREE.SphereGeometry(0.2, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), mat(C.log, 0.8), 0, 0.12, 0));
const rug = () => G(M(new THREE.PlaneGeometry(1.6, 2.2), mat(C.flowerR, 0.95), 0, 0.02, 0, 0, -Math.PI / 2));
const shield = () => G(M(new THREE.CylinderGeometry(0.45, 0.45, 0.08, 16), mat(C.log, 0.8), 0, 0.5, 0, 0, 0, Math.PI / 2), M(new THREE.SphereGeometry(0.1, 8, 6), mat(C.iron), 0, 0.5, 0.05));

// --------- sacred / monumental (Norse-mythology reinterpretation) ----------
function runeShrine() {
  const g = new THREE.Group();
  g.add(BOX(5, 0.6, 5, C.stoneDark, 0, 0.3, 0, 0, 1));
  [[-2, -2], [2, -2], [-2, 2], [2, 2]].forEach(([x, z]) => { const p = carvedPost(3.2, C.logDark); p.position.set(x, 0.6, z); g.add(p); });
  g.add(M(new THREE.TorusGeometry(1.4, 0.12, 6, 28), emat(C.rune, 0.7), 0, 2.4, 0, 0, -Math.PI / 2));
  g.add(CYL(0.6, 0.4, 0.4, C.iron, 0, 0.8, 0, 12));
  g.add(M(new THREE.ConeGeometry(0.4, 0.9, 8), emat(C.fire, 1), 0, 1.4, 0));
  g.add(gableTurfRoof(6, 6, 3.8, C.turf, true, true));
  return g;
}
function templeAesir() {
  const g = new THREE.Group();
  g.add(BOX(10, 1.2, 16, C.pale, 0, 0.6, 0, 0, 0.7));
  for (let i = 0; i < 5; i++) { const z = -6 + i * 3; [-4, 4].forEach((x) => { const p = carvedPost(6, C.timberDark); p.position.set(x, 1.2, z); g.add(p); }); }
  const body = timberFrame(7, 13, 6, C.timber); body.position.y = 1.2; g.add(body);
  g.add(door(6.6, 2, 3.4));
  g.add(M(new THREE.TorusGeometry(1.8, 0.16, 8, 32), emat(C.rune, 0.7), 0, 6.5, 6.55));
  g.add(gableTurfRoof(8, 15, 7.4, C.pale, true, true));
  [[-6, 7], [6, 7], [-6, -7], [6, -7]].forEach(([x, z]) => g.add(BOX(0.8, 3, 0.6, C.stoneDark, x, 1.5, z, 0, 1)));
  return g;
}
function gatheringHall() {
  const g = timberFrame(6, 9, 3.6, C.timberDark);
  g.add(door(4.6, 1.5, 2.6));
  [-2.5, 0, 2.5].forEach((z) => { g.add(win(3.05, z)); g.add(win(-3.05, z)); });
  const p1 = carvedPost(4, C.logDark); p1.position.set(-1.3, 0, 4.6); g.add(p1);
  const p2 = carvedPost(4, C.logDark); p2.position.set(1.3, 0, 4.6); g.add(p2);
  g.add(gableTurfRoof(6.6, 9.6, 3.6, C.turf, true, true));
  return g;
}

// ===================== PRODUCTION PREFAB BATCH =====================
// homes
function largeFamilyHouse() {
  const g = timberFrame(7, 8, 3.4, C.timber);
  g.add(door(4.05, 1.4, 2.4));
  [-2.2, 0, 2.2].forEach((z) => { g.add(win(3.55, z)); g.add(win(-3.55, z)); });
  const wing = timberFrame(3, 3, 2.2, C.timber); wing.position.set(2.8, 0, 2); g.add(wing);
  const wingRoof = gableTurfRoof(3.6, 3.6, 2.2); wingRoof.position.set(2.8, 0, 2); g.add(wingRoof);
  [-0.9, 0.9].forEach((x) => { const p = carvedPost(3.2, C.logDark); p.position.set(x, 0, 4.05); g.add(p); });
  g.add(gableTurfRoof(7.6, 8.6, 3.4, C.turf, true, true));
  return g;
}
function raisedFloorHouse() {
  const g = new THREE.Group();
  for (const x of [-2, 2]) for (const z of [-2.5, 0, 2.5]) g.add(CYL(0.2, 0.24, 1.4, C.logDark, x, 0.7, z, 8));
  g.add(BOX(5.2, 0.3, 6.2, C.timberDark, 0, 1.45, 0));
  const body = timberFrame(4.6, 5.6, 2.8, C.log); body.position.y = 1.6; g.add(body);
  g.add(door(2.85, 1, 2).translateY(1.6));
  g.add(win(-1.4, 2.85, 3.2)); g.add(win(1.4, 2.85, 3.2));
  const r = gableTurfRoof(5.2, 6.2, 2.8, C.turf, false, true); r.position.y = 1.6; g.add(r);
  [-0.4, 0.4].forEach((x) => g.add(BOX(0.1, 1.6, 0.1, C.logDark, x, 0.8, 3.3)));
  for (let i = 0; i < 4; i++) g.add(BOX(0.9, 0.08, 0.08, C.log, 0, 0.4 + i * 0.35, 3.3));
  return g;
}
function fortifiedHomestead() {
  const g = new THREE.Group();
  g.add(timberFrame(5, 6, 3, C.timberDark));
  g.add(door(3.05, 1.2, 2.2)); g.add(win(-1.6, 3.05)); g.add(win(1.6, 3.05));
  g.add(gableTurfRoof(5.6, 6.6, 3, C.turf, true, true));
  const ring = 5.5;
  for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2, x = Math.cos(a) * ring, z = Math.sin(a) * ring; if (z > 4 && Math.abs(x) < 1.3) continue; g.add(CYL(0.16, 0.18, 2.2, i % 2 ? C.log : C.logLight, x, 1.1, z, 6)); g.add(CONE(0.18, 0.3, C.logDark, x, 2.35, z, 6)); }
  [[-ring, -ring], [ring, -ring], [-ring, ring], [ring, ring]].forEach(([x, z]) => { const p = carvedPost(3, C.logDark); p.position.set(x * 0.82, 0, z * 0.82); g.add(p); });
  return g;
}
// working / settlement
function warehouse() {
  const g = timberFrame(6, 8, 3, C.timber);
  g.add(BOX(2, 2.4, 0.3, C.timberDark, 0, 1.2, 4.05));
  g.add(BOX(1.8, 2.2, 0.14, C.log, 0, 1.1, 4.12));
  [-2, 2].forEach((z) => g.add(win(3.05, z, 2.2)));
  g.add(gableTurfRoof(6.6, 8.6, 3, C.timber));
  return g;
}
function carpenterWorkshop() {
  const g = timberFrame(5, 4.5, 2.6, C.log);
  g.add(door(2.3, 1.4)); g.add(win(1.8, 2.3));
  g.add(BOX(3, 0.18, 2, C.timberDark, 3, 2.2, 0));
  [-0.8, 0.8].forEach((z) => g.add(BOX(0.16, 2.2, 0.16, C.logDark, 4.3, 1.1, z)));
  for (let i = 0; i < 3; i++) g.add(M(new THREE.CylinderGeometry(0.12, 0.12, 1.8, 8), mat(i % 2 ? C.log : C.logLight), 3, 0.25 + i * 0.26, 0, 0, 0, Math.PI / 2));
  g.add(gableTurfRoof(5.6, 5, 2.6, C.timber));
  return g;
}
// halls / community
function feastHall() {
  const g = timberFrame(7, 14, 4, C.timberDark);
  g.add(door(7.05, 1.8, 2.8));
  [-5, -2.5, 0, 2.5, 5].forEach((z) => { g.add(win(3.55, z)); g.add(win(-3.55, z)); });
  [-1.4, 1.4].forEach((x) => { const p = carvedPost(4.4, C.logDark); p.position.set(x, 0, 7.05); g.add(p); });
  g.add(M(new THREE.TorusGeometry(0.9, 0.09, 6, 28), emat(C.rune, 0.7), 0, 4.2, 7.08));
  g.add(gableTurfRoof(7.6, 14.6, 4, C.turf, true, true));
  return g;
}
function councilHall() {
  const g = new THREE.Group();
  g.add(BOX(8, 0.8, 8, C.stoneDark, 0, 0.4, 0, 0, 1));
  const body = timberFrame(6, 6, 3.6, C.timber); body.position.y = 0.4; g.add(body);
  g.add(door(3.4, 1.4, 2.6));
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; const p = carvedPost(3.4, C.logDark); p.position.set(Math.cos(a) * 3.6, 0.4, Math.sin(a) * 3.6); g.add(p); }
  g.add(gableTurfRoof(6.6, 6.6, 4, C.turf, true, true));
  return g;
}
function warriorHall() {
  const g = timberFrame(7, 12, 4, C.timber);
  g.add(door(6.05, 1.6, 2.8));
  [-4, -1.5, 1.5, 4].forEach((z) => { g.add(win(3.55, z)); g.add(win(-3.55, z)); });
  for (let i = 0; i < 6; i++) { const z = -4 + i * 1.6; [3.6, -3.6].forEach((x) => g.add(M(new THREE.CylinderGeometry(0.4, 0.4, 0.08, 14), mat(i % 2 ? C.flowerR : C.bronze, 0.7), x, 2.4, z, 0, 0, Math.PI / 2))); }
  [-0.5, 0.5].forEach((rz) => g.add(M(new THREE.BoxGeometry(0.08, 3, 0.08), mat(C.logDark), 0, 3.4, 6.1, 0, 0, rz)));
  g.add(gableTurfRoof(7.6, 12.6, 4, C.timberDark, true, true));
  return g;
}
// sacred / monumental
function templeThor() {
  const g = new THREE.Group();
  g.add(BOX(9, 1, 13, C.stoneDark, 0, 0.5, 0, 0, 0.8));
  const body = timberFrame(6, 10, 5, C.timberDark); body.position.y = 0.5; g.add(body);
  g.add(door(5.1, 1.8, 3));
  g.add(BOX(0.4, 3, 0.4, C.iron, 0, 6, 5.1)); g.add(BOX(2.2, 0.8, 0.5, C.iron, 0, 5, 5.1)); // Mjölnir motif
  [-2.2, 2.2].forEach((x) => { const p = carvedPost(5, C.logDark); p.position.set(x, 0.5, 5.1); g.add(p); });
  g.add(gableTurfRoof(7, 11, 5.5, C.stoneDark, true, true));
  [[-3.5, 5.5], [3.5, 5.5]].forEach(([x, z]) => g.add(BOX(0.7, 3, 0.6, C.stone, x, 1.5, z, 0, 1)));
  return g;
}
function odinShrineHall() {
  const g = new THREE.Group();
  g.add(BOX(7, 0.8, 9, C.pale, 0, 0.4, 0, 0, 0.7));
  const body = timberFrame(5, 7, 4, C.timber); body.position.y = 0.4; g.add(body);
  g.add(door(3.6, 1.4, 2.6));
  [-1.6, 1.6].forEach((x) => { const p = carvedPost(4.2, C.logDark); p.position.set(x, 0.4, 3.6); g.add(p); g.add(SPH(0.22, C.logDark, x, 4.8, 3.6)); });
  g.add(M(new THREE.TorusGeometry(0.9, 0.1, 6, 28), emat(C.rune, 0.7), 0, 4.4, 3.62));
  g.add(gableTurfRoof(5.6, 7.6, 4.4, C.pale, true, true));
  return g;
}
function yggdrasilSanctuary() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(7, 7, 0.6, 12), mat(C.stoneDark, 1), 0, 0.3, 0));
  g.add(CYL(0.8, 1.4, 8, C.yggBark, 0, 4, 0, 9));
  for (let i = 0; i < 10; i++) g.add(SPH(rnd(1.5, 2.4), Math.random() > 0.4 ? C.yggLeaf : C.leafB, rnd(-3, 3), rnd(8, 11), rnd(-3, 3)));
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; const p = carvedPost(4, C.logDark); p.position.set(Math.cos(a) * 5, 0.6, Math.sin(a) * 5); g.add(p); }
  g.add(M(new THREE.TorusGeometry(5, 0.12, 6, 40), emat(C.runeGlow, 0.6), 0, 4, 0, 0, -Math.PI / 2));
  return g;
}
function sacredLonghouse() {
  const g = timberFrame(6, 16, 4, C.timberDark);
  g.add(door(8.05, 1.6, 2.8));
  [-6, -3, 0, 3, 6].forEach((z) => { g.add(win(3.05, z)); g.add(win(-3.05, z)); });
  [-1.4, 1.4].forEach((x) => { const p = carvedPost(4.6, C.logDark); p.position.set(x, 0, 8.05); g.add(p); });
  g.add(M(new THREE.TorusGeometry(1, 0.1, 6, 28), emat(C.rune, 0.7), 0, 4.3, 8.08));
  [[-2.6, 7.6], [2.6, 7.6], [-2.6, -7.6], [2.6, -7.6]].forEach(([x, z]) => g.add(BOX(0.6, 2.2, 0.4, C.stoneDark, x, 1.1, z, 0, 1)));
  g.add(gableTurfRoof(6.6, 16.6, 4, C.turf, true, true));
  return g;
}
function monumentalTemple() {
  const g = new THREE.Group();
  g.add(BOX(14, 1.6, 20, C.pale, 0, 0.8, 0, 0, 0.6));
  g.add(BOX(16, 0.5, 4, C.pale, 0, 0.25, 11, 0, 0.6));
  for (let i = 0; i < 6; i++) { const z = -7 + i * 2.8; [-5, -3, 3, 5].forEach((x) => { const p = carvedPost(8, C.timberDark); p.position.set(x, 1.6, z); g.add(p); }); }
  const body = timberFrame(8, 15, 8, C.timber); body.position.y = 1.6; g.add(body);
  g.add(door(8.1, 2.4, 4));
  g.add(M(new THREE.TorusGeometry(2.4, 0.2, 8, 40), emat(C.rune, 0.7), 0, 8.5, 8.15));
  g.add(gableTurfRoof(10, 17, 9.6, C.pale, true, true));
  [[-7, 9], [7, 9]].forEach(([x, z]) => { g.add(BOX(1, 4, 0.8, C.stoneDark, x, 2, z, 0, 1)); g.add(M(new THREE.ConeGeometry(0.4, 1, 8), emat(C.fire, 1), x, 4.3, z)); });
  return g;
}

// ===== BATCH #3: settlement utility + defensive prefab structures =====
function simpleBarn() { const g = timberFrame(5, 7, 3.4, C.timberDark); g.add(BOX(2.2, 2.6, 0.3, C.timberDark, 0, 1.3, 3.55)); g.add(BOX(2, 2.4, 0.14, C.log, 0, 1.2, 3.62)); g.add(gableTurfRoof(5.6, 7.6, 3.4, C.timber, true, false)); return g; }
function animalShelter() { const g = new THREE.Group(); [-1.8, 1.8].forEach((x) => [-1.4, 1.4].forEach((z) => g.add(CYL(0.14, 0.16, 2.4, C.logDark, x, 1.2, z, 7)))); g.add(BOX(4.2, 2.6, 0.3, C.log, 0, 1.3, -1.5, 0, 0.95)); g.add(gableTurfRoof(4.4, 3.4, 2.4, C.turf, false, false)); return g; }
function grainStorageShelter() { const g = new THREE.Group(); [-1, 1].forEach((x) => [-1, 1].forEach((z) => g.add(CYL(0.16, 0.2, 1.6, C.logDark, x, 0.8, z, 7)))); const body = timberFrame(2.4, 2.4, 2, C.log); body.position.y = 1.6; g.add(body); g.add(gableTurfRoof(2.8, 2.8, 3.6, C.turf, true, false)); return g; }
function smallGranary() { const g = new THREE.Group(); g.add(M(new THREE.CylinderGeometry(1.4, 1.6, 3, 10), mat(C.log, 0.95), 0, 1.5, 0)); for (let i = 0; i < 3; i++) g.add(M(new THREE.TorusGeometry(1.45, 0.06, 6, 12), mat(C.iron, 0.7), 0, 0.6 + i * 0.9, 0, 0, -Math.PI / 2)); g.add(M(new THREE.ConeGeometry(1.7, 1.2, 10), mat(C.turf), 0, 3.6, 0)); g.add(door(1.5, 1, 1.8)); return g; }
function smokehouse() { const g = timberFrame(3, 3, 3, C.timberDark); g.add(door(1.55, 0.9, 1.9)); g.add(M(new THREE.ConeGeometry(2, 1.4, 4), mat(C.timberDark), 0, 3.7, 0, Math.PI / 4)); g.add(CYL(0.25, 0.3, 0.6, C.stoneDark, 0.6, 4.4, 0, 8)); return g; }
function communalStorage() { const g = timberFrame(6, 4, 2.8, C.log); g.add(BOX(2.4, 2.2, 0.3, C.timberDark, 0, 1.1, 2.05)); [-2, 2].forEach((z) => g.add(win(3.05, z, 2))); g.add(gableTurfRoof(6.6, 4.6, 2.8, C.turf, false, false)); return g; }
function coveredMarketStand() { const g = new THREE.Group(); [-1.4, 1.4].forEach((x) => g.add(CYL(0.12, 0.14, 2.2, C.logDark, x, 1.1, -0.8, 7))); g.add(BOX(3.2, 0.8, 1.2, C.log, 0, 1, 0.3, 0, 0.9)); const r = BOX(3.4, 0.14, 2, C.timber, 0, 2.3, 0, 0, 0.9); r.rotation.x = 0.3; g.add(r); for (let i = 0; i < 3; i++) g.add(SPH(0.15, i % 2 ? C.flowerR : C.amber, -0.8 + i * 0.8, 1.5, 0.3)); return g; }
function marketPavilion() { const g = new THREE.Group(); [-2, 2].forEach((x) => [-2, 2].forEach((z) => { const p = carvedPost(3, C.logDark); p.position.set(x, 0, z); g.add(p); })); g.add(gableTurfRoof(5, 5, 3, C.turf, true, true)); [-1.3, 1.3].forEach((x) => g.add(BOX(2.6, 0.8, 1, C.log, x, 0.9, 1.3, 0, 0.9))); return g; }
function assemblyStructure() { const g = new THREE.Group(); g.add(M(new THREE.CylinderGeometry(3, 3.2, 0.5, 10), mat(C.stoneDark, 1), 0, 0.25, 0)); for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; const p = carvedPost(2.8, C.logDark); p.position.set(Math.cos(a) * 2.4, 0.5, Math.sin(a) * 2.4); g.add(p); } g.add(M(new THREE.ConeGeometry(3.2, 1.6, 10), mat(C.turf), 0, 4, 0)); g.add(M(new THREE.TorusGeometry(2.4, 0.1, 6, 24), emat(C.rune, 0.6), 0, 3, 0, 0, -Math.PI / 2)); return g; }
function animalPen() { const g = new THREE.Group(); const R = 3; for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2; if (a > 2.7 && a < 3.5) continue; g.add(CYL(0.08, 0.1, 1, C.logDark, Math.cos(a) * R, 0.5, Math.sin(a) * R, 6)); } g.add(BOX(2.4, 1.8, 0.3, C.log, 0, 0.9, -R + 0.2, 0, 0.95)); const r = gableTurfRoof(2.6, 1.6, 1.8, C.turf, false, false); r.position.set(0, 0, -R + 0.8); g.add(r); return g; }
function cartShelter() { const g = new THREE.Group(); [-1.4, 1.4].forEach((x) => [-1, 1].forEach((z) => g.add(CYL(0.12, 0.14, 2.4, C.logDark, x, 1.2, z, 7)))); g.add(gableTurfRoof(3.2, 2.6, 2.4, C.turf, false, false)); [-0.6, 0.6].forEach((x) => g.add(M(new THREE.CylinderGeometry(0.5, 0.5, 0.15, 10), mat(C.logDark), x, 0.5, 0.4, 0, 0, Math.PI / 2))); g.add(BOX(1.6, 0.3, 1, C.log, 0, 0.7, 0.4, 0, 0.95)); return g; }
function palisadeTower() { const g = new THREE.Group(); [-1, 1].forEach((x) => [-1, 1].forEach((z) => g.add(CYL(0.18, 0.22, 4, C.logDark, x, 2, z, 8)))); g.add(BOX(2.4, 0.18, 2.4, C.log, 0, 4.05, 0, 0, 0.95)); [-1.1, 1.1].forEach((x) => g.add(BOX(0.12, 0.9, 2.4, C.logDark, x, 4.5, 0))); [-1.1, 1.1].forEach((z) => g.add(BOX(2.4, 0.9, 0.12, C.logDark, 0, 4.5, z))); g.add(M(new THREE.ConeGeometry(2, 1.4, 4), mat(C.turf), 0, 5.8, 0, Math.PI / 4)); return g; }
function smallGatehouse() { const g = new THREE.Group(); [-1.6, 1.6].forEach((x) => { const body = timberFrame(1.4, 2, 3.4, C.timberDark); body.position.x = x; g.add(body); }); g.add(BOX(2.4, 0.6, 0.6, C.beam, 0, 3.3, 0)); [-0.6, 0.6].forEach((x) => g.add(BOX(0.9, 2.8, 0.16, x < 0 ? C.log : C.logLight, x, 1.4, 0, 0, 0.95))); g.add(gableTurfRoof(4.6, 2.6, 3.6, C.turf, true, true)); const dh = dragonHead(1.2, C.bronze); dh.position.set(0, 3.8, 0); dh.rotation.y = Math.PI / 2; g.add(dh); return g; }

const PREFAB_BUILDERS = {
  worker_hut: hut, log_cottage: logCottage, log_home_medium: logHomeMedium, longhouse,
  storage_shed: storageShed, workshop, hunter_cabin: hunterCabin, blacksmith, stable,
  market_stall: marketStall, market_shelter: marketShelter, guard_hut: guardHut,
  watchtower, communal_house: communalHouse,
  carved_timber_home: carvedTimberHome, wealthy_house: wealthyHouse, chieftain_hall: chieftainHall,
  rune_shrine: runeShrine, temple_aesir: templeAesir, gathering_hall: gatheringHall,
  // production batch — homes / working / halls / sacred
  large_family_house: largeFamilyHouse, raised_floor_house: raisedFloorHouse, fortified_homestead: fortifiedHomestead,
  warehouse, carpenter_workshop: carpenterWorkshop,
  feast_hall: feastHall, council_hall: councilHall, warrior_hall: warriorHall,
  temple_thor: templeThor, odin_shrine_hall: odinShrineHall, yggdrasil_sanctuary: yggdrasilSanctuary,
  sacred_longhouse: sacredLonghouse, monumental_temple: monumentalTemple,
  // batch #3 utility + defensive structures
  simple_barn: simpleBarn, animal_shelter: animalShelter, grain_storage_shelter: grainStorageShelter,
  small_granary: smallGranary, smokehouse: smokehouse, communal_storage: communalStorage,
  covered_market_stand: coveredMarketStand, market_pavilion: marketPavilion, assembly_structure: assemblyStructure,
  animal_pen: animalPen, cart_shelter: cartShelter, palisade_tower: palisadeTower, small_gatehouse: smallGatehouse,
  // legacy ids kept working
  norse_cottage: logCottage, timber_house: carvedTimberHome, viking_longhouse: longhouse,
};
const INTERIOR_BUILDERS = {
  bed_simple: bedSimple, bed_better: bedBetter, bench, stool, chair, table, long_table: longTable,
  side_table: sideTable, settle, chest, shelf,
  candle, candle_cluster: candleCluster, lantern, hanging_lantern: hangingLantern,
  wall_torch: wallTorch, standing_torch: standingTorch, hearth,
  barrel, crate, pottery, drinking_horn: drinkingHorn, wooden_bowl: woodenBowl, rug, shield,
};

// ===================== BATCH #4: INTERIOR ESSENTIALS =====================
// Permanent native interior catalog (furniture / storage / lighting / structural
// interior / soft furnishings). Shared helpers + materials; emissive-only glow
// (NO per-object dynamic lights) for performance; modest poly counts.
const leg = (x, z, h, c = C.logDark, t = 0.1) => BOX(t, h, t, c, x, h / 2, z);
const legs4 = (w, d, h, c = C.logDark, t = 0.1) => [[-w, -d], [w, -d], [-w, d], [w, d]].map(([x, z]) => leg(x, z, h, c, t));

// ---- BEDS ----
function bedRusticTimber() {
  const g = new THREE.Group();
  g.add(BOX(1.45, 0.3, 2.2, C.log, 0, 0.45, 0, 0, 0.95));
  [[-0.68, -1.05], [0.68, -1.05], [-0.68, 1.05], [0.68, 1.05]].forEach(([x, z]) => g.add(M(new THREE.CylinderGeometry(0.1, 0.12, 0.6, 8), mat(C.logDark), x, 0.3, z)));
  g.add(BOX(1.35, 0.22, 2.0, C.hide, 0, 0.68, 0.05));
  g.add(BOX(1.35, 0.3, 0.55, C.pale, 0, 0.72, -0.72));
  g.add(BOX(1.5, 0.9, 0.18, C.timberDark, 0, 0.75, -1.08));
  return g;
}
function bedCarvedMidgard() {
  const g = new THREE.Group();
  g.add(BOX(1.5, 0.4, 2.3, C.timber, 0, 0.4, 0, 0, 0.9));
  g.add(BOX(1.4, 0.24, 2.1, C.hide, 0, 0.64, 0.05));
  g.add(BOX(1.4, 0.3, 0.55, C.flowerR, 0, 0.68, -0.78));
  g.add(BOX(1.55, 1.2, 0.2, C.timberDark, 0, 0.9, -1.12));
  g.add(runeRow(5, 1.2, 1.3, -1.0, 0.18));
  [-0.72, 0.72].forEach((x) => { const p = carvedPost(1.6, C.logDark); p.position.set(x, 0, -1.12); p.scale.setScalar(0.6); g.add(p); });
  return g;
}
function bedDouble() {
  const g = new THREE.Group();
  g.add(BOX(2.1, 0.4, 2.3, C.timber, 0, 0.4, 0, 0, 0.9));
  g.add(BOX(2.0, 0.26, 2.1, C.hide, 0, 0.66, 0.05));
  [-0.5, 0.5].forEach((x) => g.add(BOX(0.8, 0.3, 0.55, C.pale, x, 0.7, -0.78)));
  g.add(BOX(2.15, 1.0, 0.2, C.timberDark, 0, 0.85, -1.12));
  g.add(runeRow(6, 1.7, 1.1, -1.0, 0.16));
  [[-1.0, -1.1], [1.0, -1.1], [-1.0, 1.1], [1.0, 1.1]].forEach(([x, z]) => g.add(BOX(0.16, 0.5, 0.16, C.logDark, x, 0.25, z)));
  return g;
}
function bedWealthy() {
  const g = new THREE.Group();
  g.add(BOX(2.1, 0.5, 2.4, C.timberDark, 0, 0.45, 0, 0, 0.8));
  g.add(BOX(2.0, 0.28, 2.2, C.flowerR, 0, 0.74, 0.05));
  [-0.55, 0.55].forEach((x) => g.add(BOX(0.85, 0.32, 0.55, C.pale, x, 0.8, -0.82)));
  [[-1.0, -1.15], [1.0, -1.15], [-1.0, 1.15], [1.0, 1.15]].forEach(([x, z]) => g.add(M(new THREE.CylinderGeometry(0.08, 0.1, 2.4, 8), mat(C.bronze, 0.5, { metalness: 0.4 }), x, 1.2, z)));
  g.add(BOX(2.2, 0.12, 0.12, C.bronze, 0, 2.4, -1.15, 0, 0.5));
  g.add(BOX(2.2, 0.12, 0.12, C.bronze, 0, 2.4, 1.15, 0, 0.5));
  [-1.0, 1.0].forEach((x) => g.add(BOX(0.12, 0.12, 2.3, C.bronze, x, 2.4, 0, 0, 0.5)));
  g.add(BOX(2.2, 1.3, 0.2, C.timberDark, 0, 0.95, -1.18));
  g.add(runeRow(6, 1.7, 1.4, -1.06, 0.18, C.gold));
  return g;
}

// ---- SEATING ----
function chairCarved() {
  const g = new THREE.Group();
  g.add(BOX(0.5, 0.1, 0.5, C.timber, 0, 0.48, 0));
  g.add(BOX(0.5, 0.75, 0.1, C.timberDark, 0, 0.85, -0.2));
  g.add(runeRow(3, 0.4, 1.0, -0.18, 0.12));
  legs4(0.2, 0.2, 0.48, C.logDark, 0.08).forEach((l) => g.add(l));
  return g;
}
function benchLongHall() {
  const g = new THREE.Group();
  g.add(BOX(3.0, 0.16, 0.5, C.timber, 0, 0.48, 0, 0, 0.9));
  [-1.3, 0, 1.3].forEach((x) => g.add(BOX(0.2, 0.48, 0.5, C.logDark, x, 0.24, 0)));
  g.add(BOX(3.0, 0.1, 0.1, C.logDark, 0, 0.3, 0));
  return g;
}
function chairHighBack() {
  const g = new THREE.Group();
  g.add(BOX(0.55, 0.12, 0.55, C.timber, 0, 0.48, 0));
  g.add(BOX(0.55, 1.3, 0.12, C.timberDark, 0, 1.1, -0.22));
  g.add(runeRow(4, 0.45, 1.5, -0.2, 0.14));
  const dh = dragonHead(0.5, C.bronze); dh.position.set(0, 1.75, -0.22); dh.rotation.z = 0.4; g.add(dh);
  legs4(0.22, 0.22, 0.48, C.logDark, 0.1).forEach((l) => g.add(l));
  return g;
}
function seatHideWealthy() {
  const g = new THREE.Group();
  g.add(BOX(1.3, 0.4, 0.8, C.timberDark, 0, 0.3, 0, 0, 0.85));
  g.add(BOX(1.25, 0.25, 0.75, C.hide, 0, 0.58, 0.02));
  g.add(BOX(1.3, 0.5, 0.2, C.hide, 0, 0.75, -0.3));
  [-0.55, 0.55].forEach((x) => g.add(BOX(0.12, 0.6, 0.8, C.timber, x, 0.5, 0, 0, 0.8)));
  g.add(runeRow(4, 0.9, 0.95, -0.38, 0.14, C.gold));
  return g;
}

// ---- TABLES ----
function tableSmall() {
  const g = new THREE.Group();
  g.add(BOX(0.9, 0.12, 0.7, C.timber, 0, 0.72, 0, 0, 0.9));
  legs4(0.38, 0.28, 0.72, C.logDark, 0.1).forEach((l) => g.add(l));
  return g;
}
function tableLargeDining() {
  const g = new THREE.Group();
  g.add(BOX(2.4, 0.16, 1.1, C.timber, 0, 0.9, 0, 0, 0.9));
  g.add(BOX(2.45, 0.06, 1.15, C.beam, 0, 0.82, 0));
  [-1.0, 1.0].forEach((x) => g.add(BOX(0.25, 0.9, 0.95, C.logDark, x, 0.45, 0)));
  g.add(BOX(2.0, 0.12, 0.14, C.logDark, 0, 0.35, 0));
  return g;
}
function tableFeastLong() {
  const g = new THREE.Group();
  g.add(BOX(4.0, 0.18, 1.2, C.timberDark, 0, 0.92, 0, 0, 0.9));
  [-1.7, 0, 1.7].forEach((x) => g.add(BOX(0.3, 0.92, 1.0, C.logDark, x, 0.46, 0)));
  g.add(BOX(3.6, 0.14, 0.16, C.logDark, 0, 0.3, 0));
  g.add(runeRow(7, 3.4, 0.99, 0.6, 0.16));
  return g;
}
function tableWork() {
  const g = new THREE.Group();
  g.add(BOX(1.8, 0.2, 0.9, C.log, 0, 0.88, 0, 0, 0.98));
  legs4(0.78, 0.38, 0.88, C.timberDark, 0.16).forEach((l) => g.add(l));
  g.add(BOX(1.7, 0.1, 0.1, C.timberDark, 0, 0.3, 0.38));
  return g;
}
function tableCeremonial() {
  const g = new THREE.Group();
  g.add(BOX(1.4, 0.16, 0.9, C.stoneDress, 0, 0.9, 0, 0, 0.8));
  g.add(M(new THREE.TorusGeometry(0.45, 0.04, 6, 24), emat(C.runeGlow, 0.6), 0, 0.99, 0, 0, -Math.PI / 2));
  [-0.55, 0.55].forEach((x) => { const p = carvedPost(0.9, C.logDark); p.position.set(x, 0, 0); p.scale.setScalar(0.7); g.add(p); });
  return g;
}

// ---- STORAGE (catalog/build assets only; no container gameplay) ----
function chestLarge() {
  const g = new THREE.Group();
  g.add(BOX(1.4, 0.7, 0.8, C.log, 0, 0.35, 0, 0, 0.95));
  g.add(M(new THREE.CylinderGeometry(0.4, 0.4, 1.4, 12, 1, false, 0, Math.PI), mat(C.logLight), 0, 0.7, 0, 0, 0, Math.PI / 2));
  g.add(BOX(1.42, 0.12, 0.82, C.iron, 0, 0.62, 0));
  [-0.4, 0.4].forEach((x) => g.add(BOX(0.1, 0.72, 0.84, C.iron, x, 0.38, 0)));
  return g;
}
function chestCarved() {
  const g = new THREE.Group();
  g.add(BOX(1.1, 0.6, 0.65, C.timber, 0, 0.3, 0, 0, 0.9));
  g.add(BOX(1.12, 0.14, 0.67, C.timberDark, 0, 0.6, 0));
  g.add(runeRow(5, 0.9, 0.3, 0.34, 0.14));
  const dh = dragonHead(0.5, C.bronze); dh.position.set(0, 0.66, 0); dh.rotation.x = -0.3; g.add(dh);
  [-0.45, 0.45].forEach((x) => g.add(BOX(0.08, 0.62, 0.67, C.iron, x, 0.31, 0)));
  return g;
}
function storageBench() {
  const g = new THREE.Group();
  g.add(BOX(1.8, 0.5, 0.6, C.log, 0, 0.25, 0, 0, 0.95));
  g.add(BOX(1.82, 0.12, 0.62, C.timberDark, 0, 0.52, 0));
  g.add(BOX(1.8, 0.5, 0.16, C.timber, 0, 0.75, -0.22));
  g.add(runeRow(5, 1.4, 0.5, 0.31, 0.12));
  return g;
}
function shelvingOpen() {
  const g = new THREE.Group();
  [-0.85, 0.85].forEach((x) => g.add(BOX(0.12, 1.8, 0.4, C.logDark, x, 0.9, 0)));
  [0.3, 0.9, 1.5].forEach((y) => g.add(BOX(1.7, 0.08, 0.4, C.timber, 0, y, 0, 0, 0.9)));
  g.add(BOX(1.7, 0.1, 0.1, C.logDark, 0, 1.78, -0.15));
  return g;
}
function cupboardStanding() {
  const g = new THREE.Group();
  g.add(BOX(1.2, 2.0, 0.55, C.timber, 0, 1.0, 0, 0, 0.9));
  [-0.3, 0.3].forEach((x) => g.add(BOX(0.56, 1.7, 0.04, C.timberDark, x, 1.0, 0.28)));
  [-0.05, 0.05].forEach((x) => g.add(SPH(0.05, C.iron, x, 1.0, 0.32)));
  g.add(BOX(1.26, 0.16, 0.6, C.timberDark, 0, 2.02, 0));
  g.add(runeRow(4, 0.9, 1.9, 0.29, 0.12));
  return g;
}
function weaponRack() {
  const g = new THREE.Group();
  g.add(BOX(1.6, 0.15, 0.4, C.log, 0, 0.1, 0, 0, 0.95));
  [-0.75, 0.75].forEach((x) => g.add(BOX(0.12, 1.6, 0.12, C.logDark, x, 0.8, 0)));
  [0.5, 1.4].forEach((y) => g.add(BOX(1.6, 0.1, 0.12, C.timberDark, 0, y, 0)));
  [-0.5, 0, 0.5].forEach((x) => g.add(M(new THREE.CylinderGeometry(0.03, 0.03, 1.5, 6), mat(C.timber), x, 0.85, 0.1)));
  [-0.5, 0.5].forEach((x) => g.add(M(new THREE.CylinderGeometry(0.22, 0.22, 0.06, 14), mat(C.iron, 0.6), x, 1.1, 0.14, 0, Math.PI / 2, 0)));
  return g;
}
function cabinetFood() {
  const g = new THREE.Group();
  g.add(BOX(1.1, 1.5, 0.5, C.log, 0, 0.75, 0, 0, 0.95));
  for (let i = 0; i < 6; i++) g.add(BOX(0.9, 0.04, 0.04, C.logDark, 0, 0.4 + i * 0.18, 0.26));
  g.add(BOX(1.14, 0.14, 0.54, C.timberDark, 0, 1.52, 0));
  [-0.25, 0.25].forEach((x) => g.add(SPH(0.04, C.iron, x, 0.75, 0.28)));
  return g;
}
function chestHallLarge() {
  const g = new THREE.Group();
  g.add(BOX(2.0, 0.9, 1.0, C.timberDark, 0, 0.45, 0, 0, 0.9));
  g.add(M(new THREE.CylinderGeometry(0.55, 0.55, 2.0, 12, 1, false, 0, Math.PI), mat(C.timber), 0, 0.9, 0, 0, 0, Math.PI / 2));
  [-0.6, 0, 0.6].forEach((x) => g.add(BOX(0.12, 0.92, 1.04, C.iron, x, 0.46, 0)));
  g.add(runeRow(6, 1.6, 0.45, 0.52, 0.16, C.gold));
  return g;
}

// ---- LIGHTING + FIRE (emissive glow only) ----
function wallLight() {
  const g = new THREE.Group();
  g.add(BOX(0.2, 0.3, 0.12, C.iron, 0, 0, 0));
  g.add(M(new THREE.CylinderGeometry(0.04, 0.04, 0.4, 6), mat(C.logDark), 0, 0.12, 0.2, 0, Math.PI / 2, 0));
  g.add(M(new THREE.SphereGeometry(0.14, 10, 8), emat(C.glow, 0.9), 0, 0.2, 0.38));
  return g;
}
function brazierSmall() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(0.35, 0.22, 0.3, 10), mat(C.iron, 0.6, { metalness: 0.5 }), 0, 0.7, 0));
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; g.add(CYL(0.04, 0.05, 0.7, C.iron, Math.cos(a) * 0.2, 0.35, Math.sin(a) * 0.2)); }
  g.add(M(new THREE.ConeGeometry(0.22, 0.5, 8), emat(C.fire, 1), 0, 1.0, 0));
  g.add(M(new THREE.ConeGeometry(0.12, 0.3, 8), emat(C.glow, 1), 0, 1.12, 0));
  return g;
}
function brazierRuneSacred() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(0.45, 0.6, 0.9, 10), mat(C.stoneDark, 1), 0, 0.45, 0));
  for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.add(M(new THREE.BoxGeometry(0.06, 0.16, 0.04), emat(C.runeGlow, 0.9), Math.cos(a) * 0.5, 0.5, Math.sin(a) * 0.5, a)); }
  g.add(M(new THREE.CylinderGeometry(0.5, 0.3, 0.35, 12), mat(C.iron, 0.6, { metalness: 0.5 }), 0, 1.05, 0));
  g.add(M(new THREE.TorusGeometry(0.7, 0.05, 6, 24), emat(C.runeBlue, 0.6), 0, 1.0, 0, 0, -Math.PI / 2));
  g.add(M(new THREE.ConeGeometry(0.3, 0.7, 8), emat(C.fire, 1), 0, 1.5, 0));
  g.add(M(new THREE.ConeGeometry(0.16, 0.45, 8), emat(C.glow, 1), 0, 1.65, 0));
  return g;
}
function hearthHall() {
  const g = new THREE.Group();
  g.add(BOX(2.4, 0.5, 1.0, C.stoneDark, 0, 0.25, 0, 0, 1));
  g.add(BOX(2.2, 0.2, 0.8, C.iron, 0, 0.5, 0, 0, 0.7));
  for (let i = 0; i < 5; i++) g.add(M(new THREE.ConeGeometry(0.22, rnd(0.5, 0.8), 8), emat(C.fire, 1), -0.8 + i * 0.4, 0.75, 0));
  for (let i = 0; i < 3; i++) g.add(M(new THREE.ConeGeometry(0.14, 0.4, 8), emat(C.glow, 1), -0.6 + i * 0.6, 0.9, 0));
  [-1.1, 1.1].forEach((x) => { const p = carvedPost(1.2, C.logDark); p.position.set(x, 0.5, 0); p.scale.setScalar(0.7); g.add(p); });
  return g;
}
function fixtureHangingFire() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(0.015, 0.015, 1.0, 5), mat(C.iron), 0, 1.5, 0));
  g.add(M(new THREE.TorusGeometry(0.5, 0.04, 6, 20), mat(C.iron, 0.6, { metalness: 0.5 }), 0, 1.0, 0, 0, -Math.PI / 2));
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; g.add(M(new THREE.ConeGeometry(0.1, 0.28, 6), emat(C.fire, 1), Math.cos(a) * 0.5, 1.05, Math.sin(a) * 0.5)); }
  return g;
}

// ---- INTERIOR STRUCTURAL (reuses existing grid snapping) ----
function partitionTimber() {
  const g = new THREE.Group();
  g.add(BOX(2, 2.2, 0.18, C.timber, 0, 1.1, 0, 0, 0.9));
  [-0.9, 0.9].forEach((x) => g.add(BOX(0.16, 2.2, 0.22, C.timberDark, x, 1.1, 0)));
  g.add(BOX(2, 0.18, 0.22, C.timberDark, 0, 2.1, 0));
  return g;
}
function partitionCarved() {
  const g = partitionTimber();
  g.add(runeRow(5, 1.6, 1.5, 0.11, 0.18));
  const dh = dragonHead(0.6, C.bronze); dh.position.set(0, 2.2, 0.1); dh.rotation.x = -0.3; g.add(dh);
  return g;
}
function dividerSimple() {
  const g = new THREE.Group();
  [-0.8, 0, 0.8].forEach((x) => g.add(BOX(0.14, 1.6, 0.14, C.logDark, x, 0.8, 0)));
  [0.4, 0.8, 1.2].forEach((y) => g.add(BOX(1.9, 0.08, 0.1, C.log, 0, y, 0)));
  return g;
}
function interiorWallShort() {
  const g = new THREE.Group();
  g.add(BOX(2, 1.3, 0.2, C.timber, 0, 0.65, 0, 0, 0.9));
  g.add(BOX(2.05, 0.16, 0.26, C.timberDark, 0, 1.3, 0));
  return g;
}
function partitionDoorway() {
  const g = new THREE.Group();
  [-0.85, 0.85].forEach((x) => g.add(BOX(0.3, 2.2, 0.2, C.timber, x, 1.1, 0, 0, 0.9)));
  g.add(BOX(2, 0.3, 0.24, C.timberDark, 0, 2.05, 0));
  g.add(runeRow(4, 1.0, 1.9, 0.13, 0.14));
  return g;
}
function interiorPost() { return carvedPost(2.6, C.logDark); }
function interiorPlatform() {
  const g = new THREE.Group();
  [-0.8, 0.8].forEach((x) => [-0.8, 0.8].forEach((z) => g.add(BOX(0.16, 0.3, 0.16, C.logDark, x, 0.15, z))));
  for (let i = 0; i < 5; i++) g.add(BOX(0.36, 0.1, 2, i % 2 ? C.log : C.logLight, -0.8 + i * 0.4, 0.3, 0, 0, 0.9));
  return g;
}
function interiorStep() {
  const g = new THREE.Group();
  for (let i = 0; i < 2; i++) g.add(BOX(1.4, 0.25, 0.4, i % 2 ? C.log : C.logLight, 0, 0.125 + i * 0.25, 0.3 - i * 0.4, 0, 0.95));
  return g;
}
function hearthBacking() {
  const g = new THREE.Group();
  g.add(BOX(2, 2.4, 0.4, C.stoneDark, 0, 1.2, 0, 0, 1));
  g.add(BOX(1.2, 1.0, 0.5, C.iron, 0, 0.5, 0.1, 0, 0.6));
  g.add(M(new THREE.ConeGeometry(0.9, 1.2, 4), mat(C.stone, 1), 0, 2.6, 0, Math.PI / 4));
  g.add(runeRow(4, 1.4, 1.8, 0.22, 0.16));
  return g;
}
function ceilingBeam() {
  const g = new THREE.Group();
  g.add(BOX(2, 0.25, 0.25, C.timberDark, 0, 0, 0));
  [-0.6, 0.6].forEach((x) => g.add(BOX(0.18, 0.3, 0.3, C.beam, x, 0, 0)));
  return g;
}

// ---- FLOOR COVERINGS (no collision) ----
function rugHideLarge() { return G(M(new THREE.PlaneGeometry(2.4, 3.2), mat(C.hide, 0.95), 0, 0.02, 0, 0, -Math.PI / 2)); }
function rugWoven() {
  const g = G(M(new THREE.PlaneGeometry(2.0, 2.6), mat(C.timber, 0.95), 0, 0.02, 0, 0, -Math.PI / 2));
  for (let i = 0; i < 5; i++) g.add(M(new THREE.PlaneGeometry(2.0, 0.1), mat(i % 2 ? C.flowerR : C.pale, 0.95), 0, 0.025, -1.0 + i * 0.5, 0, -Math.PI / 2));
  return g;
}
function runnerHall() { return G(M(new THREE.PlaneGeometry(1.2, 5.0), mat(C.flowerR, 0.95), 0, 0.02, 0, 0, -Math.PI / 2)); }
function rugRuneCeremonial() {
  const g = G(M(new THREE.CircleGeometry(1.6, 32), mat(C.stoneDark, 0.95), 0, 0.02, 0, 0, -Math.PI / 2));
  g.add(M(new THREE.TorusGeometry(1.2, 0.06, 6, 40), emat(C.runeGlow, 0.5), 0, 0.03, 0, 0, -Math.PI / 2));
  g.add(M(new THREE.TorusGeometry(0.8, 0.05, 6, 32), emat(C.runeBlue, 0.4), 0, 0.03, 0, 0, -Math.PI / 2));
  return g;
}

// ---- HALL / COMMUNAL ----
function highSeat() {
  const g = new THREE.Group();
  g.add(BOX(1.0, 0.5, 0.9, C.timberDark, 0, 0.5, 0, 0, 0.85));
  g.add(BOX(0.95, 0.2, 0.85, C.hide, 0, 0.78, 0.02));
  g.add(BOX(1.0, 2.2, 0.2, C.timberDark, 0, 1.3, -0.35));
  [-0.5, 0.5].forEach((x) => { const p = carvedPost(2.4, C.logDark); p.position.set(x, 0, -0.35); p.scale.set(0.8, 1, 0.8); g.add(p); });
  g.add(runeRow(5, 0.8, 2.0, -0.24, 0.18, C.gold));
  [-0.5, 0.5].forEach((x) => { const dh = dragonHead(0.7, C.gold); dh.position.set(x, 2.5, -0.35); dh.rotation.z = 0.5; g.add(dh); });
  return g;
}
function shieldRack() {
  const g = new THREE.Group();
  g.add(BOX(3.0, 0.2, 0.2, C.timberDark, 0, 1.6, 0));
  g.add(BOX(3.0, 0.2, 0.2, C.timberDark, 0, 0.4, 0));
  [-1.2, 0, 1.2].forEach((x, i) => {
    g.add(M(new THREE.CylinderGeometry(0.4, 0.4, 0.08, 16), mat(i % 2 ? C.flowerR : C.bronze, 0.7), x, 1.0, 0.14, 0, Math.PI / 2, 0));
    g.add(SPH(0.08, C.iron, x, 1.0, 0.2));
  });
  return g;
}
function hallDivider() {
  const g = new THREE.Group();
  [-1.4, 1.4].forEach((x) => { const p = carvedPost(3.0, C.logDark); p.position.set(x, 0, 0); g.add(p); });
  g.add(BOX(3.0, 0.3, 0.3, C.beam, 0, 2.9, 0));
  g.add(BOX(2.8, 1.6, 0.14, C.timber, 0, 1.6, 0, 0, 0.9));
  g.add(runeRow(6, 2.2, 1.6, 0.1, 0.18));
  return g;
}
function hearthCentral() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(1.4, 1.5, 0.4, 16), mat(C.stoneDark, 1), 0, 0.2, 0));
  g.add(M(new THREE.TorusGeometry(1.3, 0.12, 8, 24), mat(C.stone, 1), 0, 0.4, 0, 0, -Math.PI / 2));
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; g.add(M(new THREE.ConeGeometry(0.22, rnd(0.6, 0.9), 8), emat(C.fire, 1), Math.cos(a) * 0.4, 0.7, Math.sin(a) * 0.4)); }
  g.add(M(new THREE.ConeGeometry(0.3, 0.8, 8), emat(C.glow, 1), 0, 0.9, 0));
  return g;
}

// ---- WORKSHOP (furniture only; no crafting) ----
function workbenchHeavy() {
  const g = new THREE.Group();
  g.add(BOX(2.0, 0.25, 0.9, C.timberDark, 0, 0.9, 0, 0, 0.98));
  [[-0.85, -0.35], [0.85, -0.35], [-0.85, 0.35], [0.85, 0.35]].forEach(([x, z]) => g.add(BOX(0.2, 0.9, 0.2, C.logDark, x, 0.45, z)));
  g.add(BOX(1.9, 0.14, 0.14, C.logDark, 0, 0.35, 0));
  g.add(M(new THREE.BoxGeometry(0.3, 0.3, 0.3), mat(C.iron, 0.5, { metalness: 0.5 }), 0.7, 1.15, 0));
  return g;
}
function workbenchCarpenter() {
  const g = workbenchHeavy();
  for (let i = 0; i < 3; i++) g.add(M(new THREE.CylinderGeometry(0.1, 0.1, 1.6, 8), mat(i % 2 ? C.log : C.logLight), -0.4 + i * 0.4, 1.12, -0.2, 0, 0, Math.PI / 2));
  return g;
}
function workbenchSmithing() {
  const g = new THREE.Group();
  g.add(BOX(1.6, 0.3, 0.8, C.stoneDark, 0, 0.8, 0, 0, 1));
  [[-0.65, -0.3], [0.65, -0.3], [-0.65, 0.3], [0.65, 0.3]].forEach(([x, z]) => g.add(BOX(0.22, 0.8, 0.22, C.timberDark, x, 0.4, z)));
  g.add(M(new THREE.CylinderGeometry(0.25, 0.35, 0.4, 8), mat(C.iron, 0.5, { metalness: 0.6 }), -0.5, 1.1, 0));
  g.add(M(new THREE.ConeGeometry(0.2, 0.4, 8), emat(C.fire, 0.9), 0.5, 1.1, 0));
  return g;
}
function storageRack() {
  const g = new THREE.Group();
  [-0.85, 0.85].forEach((x) => g.add(BOX(0.16, 2.2, 0.5, C.logDark, x, 1.1, 0)));
  [0.4, 1.1, 1.8].forEach((y) => g.add(BOX(1.8, 0.1, 0.5, C.timber, 0, y, 0, 0, 0.9)));
  g.add(BOX(0.5, 0.4, 0.4, C.log, -0.4, 0.65, 0));
  g.add(BOX(0.4, 0.4, 0.4, C.log, 0.5, 1.35, 0));
  return g;
}
function toolRack() {
  const g = new THREE.Group();
  g.add(BOX(1.8, 0.16, 0.16, C.timberDark, 0, 1.7, 0));
  g.add(BOX(1.8, 0.12, 0.3, C.timber, 0, 0.3, 0, 0, 0.9));
  for (let i = 0; i < 4; i++) g.add(M(new THREE.CylinderGeometry(0.03, 0.03, 0.9, 6), mat(C.logDark), -0.7 + i * 0.47, 1.25, 0.08));
  for (let i = 0; i < 4; i++) g.add(M(new THREE.BoxGeometry(0.12, 0.2, 0.06), mat(C.iron, 0.5, { metalness: 0.5 }), -0.7 + i * 0.47, 0.85, 0.1));
  return g;
}
function shelfHeavy() {
  const g = new THREE.Group();
  [-0.9, 0.9].forEach((x) => g.add(BOX(0.2, 1.6, 0.5, C.timberDark, x, 0.8, 0)));
  [0.4, 1.0, 1.5].forEach((y) => g.add(BOX(1.9, 0.14, 0.5, C.log, 0, y, 0, 0, 0.95)));
  return g;
}
function stoolWorkshop() {
  const g = new THREE.Group();
  g.add(M(new THREE.CylinderGeometry(0.26, 0.28, 0.14, 10), mat(C.timberDark), 0, 0.6, 0));
  [[-0.16, -0.16], [0.16, -0.16], [-0.16, 0.16], [0.16, 0.16]].forEach(([x, z]) => g.add(M(new THREE.CylinderGeometry(0.04, 0.05, 0.6, 6), mat(C.logDark), x, 0.3, z)));
  g.add(BOX(0.4, 0.04, 0.04, C.logDark, 0, 0.2, 0));
  return g;
}

const INTERIOR2_BUILDERS = {
  bed_rustic_timber: bedRusticTimber, bed_carved_midgard: bedCarvedMidgard, bed_double: bedDouble, bed_wealthy: bedWealthy,
  chair_carved: chairCarved, bench_long_hall: benchLongHall, chair_high_back: chairHighBack, seat_hide_wealthy: seatHideWealthy,
  table_small: tableSmall, table_large_dining: tableLargeDining, table_feast_long: tableFeastLong, table_work: tableWork, table_ceremonial: tableCeremonial,
  chest_large: chestLarge, chest_carved: chestCarved, storage_bench: storageBench, shelving_open: shelvingOpen,
  cupboard_standing: cupboardStanding, weapon_rack: weaponRack, cabinet_food: cabinetFood, chest_hall_large: chestHallLarge,
  wall_light: wallLight, brazier_small: brazierSmall, brazier_rune_sacred: brazierRuneSacred, hearth_hall: hearthHall, fixture_hanging_fire: fixtureHangingFire,
  partition_timber: partitionTimber, partition_carved: partitionCarved, divider_simple: dividerSimple, interior_wall_short: interiorWallShort,
  partition_doorway: partitionDoorway, interior_post: interiorPost, interior_platform: interiorPlatform, interior_step: interiorStep,
  hearth_backing: hearthBacking, ceiling_beam: ceilingBeam,
  rug_hide_large: rugHideLarge, rug_woven: rugWoven, runner_hall: runnerHall, rug_rune_ceremonial: rugRuneCeremonial,
  high_seat: highSeat, shield_rack: shieldRack, hall_divider: hallDivider, hearth_central: hearthCentral,
  workbench_heavy: workbenchHeavy, workbench_carpenter: workbenchCarpenter, workbench_smithing: workbenchSmithing,
  storage_rack: storageRack, tool_rack: toolRack, shelf_heavy: shelfHeavy, stool_workshop: stoolWorkshop,
};

// Merge all builders
export const NATIVE_ASSET_BUILDERS = { ...NATIVE_BUILDERS, ...PREFAB_BUILDERS, ...INTERIOR_BUILDERS, ...INTERIOR2_BUILDERS };

// pretty name from id
const pn = (id) => id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Economy-ready canonical catalog schema. IDs/persistence unchanged; new fields
// (realm, styleFamily, placeable, purchasable, coinPrice, unlockRequirement,
// collisionType, module, snap) prepare assets for a future coin/ownership layer
// WITHOUT any storage migration. STRUCT subs get grid snapping metadata.
const STRUCT_Y = { Foundations: 0, Floors: 0, Walls: 0, Roofs: WALL_H, Terrain: 0, Infrastructure: 0 };
const MODULE_DIMS = { Walls: { w: 2, d: 0.3, h: 3 }, Floors: { w: 2, d: 2, h: 0.2 }, Foundations: { w: 2, d: 2, h: 0.8 }, Roofs: { w: 2, d: 2.7, h: 2 }, Terrain: { w: 2, d: 2, h: 0.1 }, Infrastructure: { w: 2, d: 2, h: 1 } };
function defs(ids, sub, collision, opts = {}) {
  const { tier = 'RUSTIC', realm = 'midgard', idPrefix = '', surface = null, ptype = null, snap: snapOverride = null } = opts;
  const hasSnap = Object.prototype.hasOwnProperty.call(STRUCT_Y, sub);
  const snap = snapOverride || (hasSnap ? { grid: 1, rotStep: Math.PI / 2, y: STRUCT_Y[sub] } : null);
  const category = sub === 'Nature' ? 'nature' : sub === 'Interior' ? 'interior' : 'construction';
  return ids.map((raw) => {
    const [id, name, t] = Array.isArray(raw) ? raw : [raw, pn(raw), tier];
    return {
      id: `midgard.native.${idPrefix}${id}`, builderKey: id, name, sub, category, subcategory: sub,
      tier: t || tier, realm, styleFamily: 'midgard-mythic', sourceType: 'native',
      placeable: true, purchasable: false, coinPrice: 0, unlockRequirement: null,
      collision, collisionType: collision, module: MODULE_DIMS[sub] || null, snap,
      placementSurface: surface, placementType: ptype,
    };
  });
}

export const NATIVE_ASSETS = [
  // NATURE
  ...defs(['grass_sparse', 'grass_medium', 'grass_dense', 'grass_tall', 'grass_edge', 'grass_cluster',
    'flowers_yellow', 'flowers_pink', 'bush_round', 'bush_wide', 'fern', 'mushroom_cluster',
    'rock_small', 'dirt_patch', 'dirt_road_straight', 'dirt_road_curve', 'dirt_road_junction', 'footpath'], 'Nature', 'none'),
  ...defs(['rock_medium', 'rock_large_flat', 'boulder', 'fallen_log', 'tree_stump',
    'tree_pine', 'tree_broadleaf', 'tree_birch', 'dead_tree'], 'Nature', 'cuboid'),
  // NATURE — mythic trees + Yggdrasil (cuboid) and natural boundaries (cuboid)
  ...defs([['yggdrasil', 'Yggdrasil (World Tree)', 'ASGARD'], ['tree_young', 'Young Tree'], ['tree_ancient_twisted', 'Ancient Twisted Tree'],
    ['tree_massive_old', 'Massive Old Tree'], ['tree_sparse', 'Sparse Tree'],
    ['hedge_strip', 'Hedge Strip'], ['hedge_long', 'Long Hedge'], ['hedge_corner', 'Hedge Corner'],
    ['bush_fence', 'Bush Fence'], ['bramble_barrier', 'Bramble Barrier']], 'Nature', 'cuboid'),
  // NATURE — soft vegetation (no collision)
  ...defs([['shrub_low', 'Low Shrub'], ['bush_cluster_wild', 'Wild Bush Cluster'],
    ['bush_flowering', 'Flowering Bush'], ['shrub_large_irregular', 'Large Irregular Shrub']], 'Nature', 'none'),
  // ===== BATCH #2: NATURE + TERRAIN =====
  // trees (cuboid)
  ...defs([['tree_ancient_broadleaf', 'Ancient Broadleaf'], ['tree_storm_damaged', 'Storm-Damaged Tree'], ['tree_leaning', 'Leaning Tree'], ['tree_woodland_cluster', 'Woodland Tree Cluster'], ['tree_sacred_grove', 'Sacred Grove Tree'], ['tree_exposed_roots', 'Exposed-Root Tree']], 'Nature', 'cuboid'),
  // natural boundaries (cuboid)
  ...defs([['hedge_end', 'Hedge End'], ['hedge_opening', 'Hedge Opening'], ['bush_barrier', 'Dense Bush Barrier'], ['mixed_boundary', 'Mixed Hedge/Bush Boundary'], ['low_natural_boundary', 'Low Natural Boundary'], ['tall_privacy_boundary', 'Tall Privacy Boundary']], 'Nature', 'cuboid'),
  // rock formations (cuboid)
  ...defs([['boulder_group', 'Medium Boulder Group'], ['boulder_walkable', 'Large Walkable Boulder'], ['cliff_low', 'Low Cliff Section'], ['cliff_large', 'Large Cliff Section'], ['stone_outcrop', 'Broken Stone Outcrop'], ['rock_mossy', 'Mossy Rock Formation'], ['rune_stone_natural', 'Rune-Marked Standing Stone']], 'Nature', 'cuboid'),
  // shrubs + grass + small rocks (no collision)
  ...defs([['bush_dense', 'Dense Bush'], ['bush_tall_wild', 'Tall Wild Bush'], ['bush_sprawling', 'Low Sprawling Bush'], ['bramble_cluster', 'Bramble Cluster'], ['shrub_woodland_dark', 'Dark Woodland Shrub'], ['bush_edge_cluster', 'Property-Edge Bush Cluster'], ['grass_strip_long', 'Long Grass Strip'], ['meadow_patch', 'Irregular Meadow Patch'], ['grass_flower_patch', 'Grass + Flower Patch'], ['woodland_ground', 'Woodland Ground Vegetation'], ['path_edge_veg', 'Path-Edge Vegetation'], ['rock_cluster_small', 'Small Rock Cluster'], ['rocky_ground_patch', 'Rocky Ground Patch'], ['rock_path_edge', 'Rocky Path Edge']], 'Nature', 'none'),
  // TERRAIN — ground / roads / transitions / banks (grid-snap, no collision)
  ...defs([['ground_grass', 'Grass Ground'], ['ground_rough_grass', 'Rough Grass Ground'], ['ground_dirt', 'Dirt Ground'], ['ground_packed_earth', 'Packed Earth'], ['ground_mud', 'Mud Ground'], ['ground_rocky', 'Rocky Ground'], ['ground_forest_floor', 'Forest Floor'], ['road_straight', 'Dirt Road Straight'], ['road_curve', 'Dirt Road Curve'], ['road_junction', 'Dirt Road Junction'], ['road_wide', 'Wide Settlement Road'], ['footpath_narrow', 'Narrow Footpath'], ['trail_worn', 'Worn Trail'], ['road_edge', 'Road Edge'], ['stone_path', 'Rough Stone Path'], ['trans_grass_dirt', 'Grass to Dirt'], ['trans_grass_rock', 'Grass to Rock'], ['trans_dirt_rock', 'Dirt to Rock'], ['trans_forest_grass', 'Forest to Grass'], ['trans_road_grass', 'Road to Grass'], ['trans_path_veg', 'Path to Vegetation'], ['bank_muddy', 'Muddy Bank'], ['bank_grassy', 'Grassy Bank'], ['bank_rocky', 'Rocky Bank'], ['wetland_reed_edge', 'Reed / Wetland Edge']], 'Terrain', 'none'),
  // FOUNDATIONS
  // ===== BATCH #3: SETTLEMENT INFRASTRUCTURE =====
  // bridges / docks / wells / platforms / stairs / gates / defensive / field-boundaries / farm (Infrastructure, cuboid, grid-snap)
  ...defs([['bridge_log_footbridge', 'Log Footbridge'], ['bridge_small_timber', 'Small Timber Bridge'], ['bridge_medium_road', 'Medium Road Bridge'], ['bridge_heavy_reinforced', 'Heavy Reinforced Bridge'], ['bridge_timber_stone', 'Timber + Stone Bridge'], ['bridge_rune_sacred', 'Rune-Carved Sacred Bridge', 'ASGARD'], ['bridge_section_short', 'Bridge Section (Short)'], ['bridge_section_long', 'Bridge Section (Long)'], ['bridge_end', 'Bridge End'], ['bridge_support', 'Bridge Support'], ['bridge_rail', 'Bridge Rail'],
    ['dock_small', 'Small Dock'], ['dock_straight', 'Dock Straight'], ['dock_end', 'Dock End'], ['dock_corner', 'Dock Corner'], ['dock_tjunction', 'Dock T-Junction'], ['pier_wooden', 'Wooden Pier'], ['fishing_platform', 'Fishing Platform'], ['loading_dock', 'Loading Dock'],
    ['well_rough', 'Rough Village Well'], ['well_covered', 'Covered Timber Well'], ['well_stone', 'Stone-Lined Well'], ['sacred_spring', 'Sacred Spring', 'ASGARD'], ['drinking_trough', 'Drinking Trough'], ['water_trough', 'Water Trough'],
    ['field_fence_low_timber', 'Low Timber Field Fence'], ['boundary_rough_log', 'Rough Log Boundary'], ['woven_branch_fence', 'Woven Branch Fence'], ['field_hedge_boundary', 'Hedge Field Boundary'],
    ['hay_shelter', 'Hay Shelter'], ['feed_trough', 'Feed Trough'], ['farm_gate', 'Farm Gate'],
    ['stair_straight', 'Timber Stair Straight'], ['stair_short', 'Short Stairs'], ['stair_wide', 'Wide Stairs'], ['stair_stone', 'Stone Stairs'], ['platform_timber', 'Timber Platform'], ['platform_raised', 'Raised Platform'], ['landing', 'Landing'], ['ramp', 'Ramp'], ['walkway_small', 'Small Raised Walkway'], ['walkway_reinforced', 'Reinforced Walkway'],
    ['property_gate_simple', 'Simple Property Gate'], ['settlement_gate_heavy', 'Heavy Settlement Gate'], ['mythic_gate', 'Mythological Gate', 'ASGARD'], ['gate_posts', 'Gate Posts'], ['carved_entrance_posts', 'Carved Entrance Posts'],
    ['watch_platform', 'Timber Watch Platform'], ['guard_platform_raised', 'Raised Guard Platform'], ['defensive_walkway', 'Defensive Walkway'], ['barricade_rough', 'Rough Barricade']], 'Infrastructure', 'cuboid'),
  // field surfaces (Terrain, no collision)
  ...defs([['field_tilled', 'Tilled Earth'], ['field_planted_rows', 'Planted Rows'], ['field_harvested', 'Harvested Field'], ['pasture_rough', 'Rough Pasture']], 'Terrain', 'none'),
  // utility + defensive prefab structures
  ...defs([['simple_barn', 'Simple Barn'], ['animal_shelter', 'Animal Shelter'], ['grain_storage_shelter', 'Grain Storage Shelter'], ['small_granary', 'Small Granary'], ['smokehouse', 'Smokehouse'], ['communal_storage', 'Communal Storage'], ['covered_market_stand', 'Covered Market Stand'], ['market_pavilion', 'Market Pavilion', 'WEALTHY'], ['assembly_structure', 'Assembly Structure', 'WEALTHY'], ['animal_pen', 'Animal Pen'], ['cart_shelter', 'Cart Shelter'], ['palisade_tower', 'Reinforced Palisade Tower', 'WEALTHY'], ['small_gatehouse', 'Small Gatehouse', 'WEALTHY']], 'Prefabs', 'cuboid'),
  ...defs([['heavy_timber_base', 'Heavy Timber Base'], ['stone_foundation', 'Stone Foundation'],
    ['raised_timber_platform', 'Raised Timber Platform'], ['stone_timber_foundation', 'Stone + Timber Foundation']], 'Foundations', 'cuboid'),
  // WALLS
  ...defs([['log_wall', 'Rough Log Wall'], ['timber_wall', 'Refined Timber Wall'], ['timber_plaster_wall', 'Timber + Plaster Wall'],
    ['rustic_stone_wall', 'Rustic Stone Wall'], ['dressed_stone_wall', 'Dressed Stone Wall', 'WEALTHY'],
    ['carved_luxury_wall', 'Carved Luxury Wall', 'ASGARD'], ['door_wall', 'Door Wall'], ['window_wall', 'Window Wall'],
    ['wall_corner', 'Wall Corner'], ['wall_end', 'Wall End'],
    ['stacked_timber_wall', 'Stacked Timber Wall'], ['heavy_beam_wall', 'Heavy Beam Wall'],
    ['timber_stone_foundation_wall', 'Timber + Stone Foundation Wall'], ['reinforced_settlement_wall', 'Reinforced Settlement Wall'],
    ['stone_timber_hybrid_wall', 'Stone + Timber Hybrid Wall'], ['gate_wall', 'Gate Section'], ['palisade_wall', 'Palisade Section'],
    ['low_wall', 'Low Wall'], ['high_defensive_wall', 'High Defensive Wall'], ['arched_door_wall', 'Arched Door Wall'],
    ['shuttered_window_wall', 'Shuttered Window Wall'], ['double_gate_wall', 'Double Gate'],
    ['palisade_corner', 'Palisade Corner'], ['palisade_gate', 'Palisade Gate']], 'Walls', 'cuboid'),
  // FLOORS
  ...defs([['plank_floor_rough', 'Rough Plank Floor'], ['wood_floor_refined', 'Refined Wood Floor'],
    ['stone_floor', 'Stone Floor'], ['flagstone_floor', 'Flagstone Floor'], ['polished_floor', 'Polished Luxury Floor', 'ASGARD'],
    ['heavy_plank_floor', 'Heavy Plank Floor'], ['rune_ceremonial_floor', 'Rune Ceremonial Floor', 'ASGARD']], 'Floors', 'cuboid'),
  // ROOFS
  ...defs([['roof_turf', 'Turf Roof'], ['roof_shingle', 'Timber Shingle Roof'], ['roof_darkwood', 'Dark Wooden Roof'],
    ['roof_steep_nordic', 'Steep Nordic Roof', 'WEALTHY'], ['roof_slate', 'Stone / Slate Roof'],
    ['roof_asgard', 'Ornate Asgard Roof', 'ASGARD'],
    ['roof_dragon_prow', 'Dragon-Prow Roof'], ['roof_ceremonial', 'Ceremonial Roof', 'ASGARD'],
    ['roof_ridge', 'Roof Ridge Piece'], ['roof_gable_end', 'Roof Gable End']], 'Roofs', 'cuboid'),
  // PREFABS
  ...defs([['worker_hut', 'Tiny Worker Hut'], ['log_cottage', 'Small Log Cottage'], ['log_home_medium', 'Medium Log Home'],
    ['longhouse', 'Family Longhouse'], ['storage_shed', 'Storage Shed'], ['workshop', 'Workshop'], ['hunter_cabin', 'Hunter Cabin'],
    ['blacksmith', 'Blacksmith'], ['stable', 'Stable'], ['market_stall', 'Market Stall'], ['market_shelter', 'Market Shelter'],
    ['guard_hut', 'Guard Hut'], ['watchtower', 'Small Watchtower'], ['communal_house', 'Communal House']], 'Prefabs', 'cuboid'),
  ...defs([['carved_timber_home', 'Carved Timber Home', 'WEALTHY'], ['wealthy_house', 'Wealthy Nordic House', 'WEALTHY'],
    ['chieftain_hall', 'Chieftain Hall', 'ASGARD'], ['gathering_hall', 'Gathering Hall', 'WEALTHY'],
    ['rune_shrine', 'Rune Shrine', 'ASGARD'], ['temple_aesir', 'Temple of the Æsir', 'ASGARD']], 'Prefabs', 'cuboid'),
  // PREFABS — production batch (homes / working / halls)
  ...defs([['large_family_house', 'Large Family House'], ['raised_floor_house', 'Raised-Floor House'], ['fortified_homestead', 'Fortified Homestead', 'WEALTHY'],
    ['warehouse', 'Small Warehouse'], ['carpenter_workshop', 'Carpenter Workshop'],
    ['feast_hall', 'Feast Hall', 'WEALTHY'], ['council_hall', 'Council Hall', 'WEALTHY'], ['warrior_hall', 'Warrior Hall', 'WEALTHY']], 'Prefabs', 'cuboid'),
  // PREFABS — sacred / monumental batch
  ...defs([['temple_thor', 'Temple of Thor', 'ASGARD'], ['odin_shrine_hall', 'Odin Shrine Hall', 'ASGARD'],
    ['yggdrasil_sanctuary', 'Yggdrasil Sanctuary', 'ASGARD'], ['sacred_longhouse', 'Great Sacred Longhouse', 'ASGARD'],
    ['monumental_temple', 'Monumental Mythic Temple', 'ASGARD']], 'Prefabs', 'cuboid'),
  // INTERIOR — furniture (collision) + small props (none)
  ...defs([['bed_simple', 'Simple Norse Bed'], ['bed_better', 'Carved Wooden Bed'], 'bench', 'stool', 'chair', 'table',
    ['long_table', 'Long Dining Table'], ['side_table', 'Side Table'], ['settle', 'Settle Seating'], ['chest', 'Storage Chest'], 'shelf',
    'hearth', 'barrel', 'crate'], 'Interior', 'cuboid'),
  ...defs(['candle', ['candle_cluster', 'Candle Cluster'], 'lantern', ['hanging_lantern', 'Hanging Lantern'],
    ['wall_torch', 'Wall Torch'], ['standing_torch', 'Standing Torch'], 'pottery', ['drinking_horn', 'Drinking Horn'],
    ['wooden_bowl', 'Wooden Bowl'], ['rug', 'Rug / Hide'], ['shield', 'Decorative Shield']], 'Interior', 'none'),
  // ===== BATCH #4: INTERIOR ESSENTIALS (placementSurface/placementType metadata) =====
  // beds / seating / tables (furniture, floor, cuboid)
  ...defs([['bed_rustic_timber', 'Rustic Timber Bed'], ['bed_carved_midgard', 'Carved Midgard Bed', 'WEALTHY'], ['bed_double', 'Large Double Bed'], ['bed_wealthy', 'Wealthy Carved Bed', 'ASGARD']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'furniture' }),
  ...defs([['chair_carved', 'Carved Chair'], ['bench_long_hall', 'Long Hall Bench'], ['chair_high_back', 'High-Backed Chair', 'WEALTHY'], ['seat_hide_wealthy', 'Hide Lounge Seat', 'WEALTHY']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'furniture' }),
  ...defs([['table_small', 'Small Table'], ['table_large_dining', 'Large Dining Table'], ['table_feast_long', 'Long Feast Table', 'WEALTHY'], ['table_work', 'Work Table'], ['table_ceremonial', 'Carved Ceremonial Table', 'ASGARD']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'furniture' }),
  // storage (storage, floor, cuboid)
  ...defs([['chest_large', 'Large Chest'], ['chest_carved', 'Carved Chest', 'WEALTHY'], ['storage_bench', 'Storage Bench'], ['shelving_open', 'Open Shelving'], ['cupboard_standing', 'Standing Cupboard'], ['weapon_rack', 'Weapon Rack'], ['cabinet_food', 'Food Cabinet'], ['chest_hall_large', 'Great Hall Chest', 'WEALTHY']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'storage' }),
  // lighting + fire (emissive only)
  ...defs([['brazier_small', 'Small Brazier'], ['brazier_rune_sacred', 'Sacred Rune Brazier', 'ASGARD'], ['hearth_hall', 'Great Hall Hearth', 'WEALTHY']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'lighting' }),
  ...defs([['wall_light', 'Wall-Mounted Light']], 'Interior', 'none', { surface: 'wall', ptype: 'lighting' }),
  ...defs([['fixture_hanging_fire', 'Hanging Fire Fixture']], 'Interior', 'none', { surface: 'ceiling', ptype: 'lighting' }),
  // interior structural (grid-snap)
  ...defs([['partition_timber', 'Timber Partition'], ['partition_carved', 'Carved Partition', 'WEALTHY'], ['divider_simple', 'Simple Room Divider'], ['interior_wall_short', 'Short Interior Wall'], ['partition_doorway', 'Doorway Partition'], ['interior_post', 'Interior Post'], ['interior_platform', 'Raised Interior Platform'], ['hearth_backing', 'Hearth Backing']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'structural-interior', snap: { grid: 1, rotStep: Math.PI / 2, y: 0 } }),
  ...defs([['interior_step', 'Interior Step']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'structural-interior', snap: { grid: 1, rotStep: Math.PI / 2, y: 0 } }),
  ...defs([['ceiling_beam', 'Ceiling Beam']], 'Interior', 'none', { surface: 'ceiling', ptype: 'structural-interior', snap: { grid: 1, rotStep: Math.PI / 2, y: 0 } }),
  // floor coverings (no collision)
  ...defs([['rug_hide_large', 'Large Hide Rug'], ['rug_woven', 'Woven Floor Covering'], ['runner_hall', 'Long Hall Runner'], ['rug_rune_ceremonial', 'Ceremonial Rune Rug', 'ASGARD']], 'Interior', 'none', { surface: 'floor', ptype: 'furniture' }),
  // hall / communal essentials
  ...defs([['high_seat', 'High Seat / Chieftain Throne', 'ASGARD']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'furniture' }),
  ...defs([['hearth_central', 'Great Central Hearth', 'WEALTHY']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'lighting' }),
  ...defs([['hall_divider', 'Hall Divider', 'WEALTHY']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'structural-interior', snap: { grid: 1, rotStep: Math.PI / 2, y: 0 } }),
  ...defs([['shield_rack', 'Shield Rack']], 'Interior', 'none', { surface: 'wall', ptype: 'furniture' }),
  // workshop essentials
  ...defs([['workbench_heavy', 'Heavy Workbench'], ['workbench_carpenter', 'Carpenter Workbench'], ['workbench_smithing', 'Smithing Table'], ['stool_workshop', 'Workshop Stool']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'furniture' }),
  ...defs([['storage_rack', 'Storage Rack'], ['shelf_heavy', 'Heavy Shelf']], 'Interior', 'cuboid', { surface: 'floor', ptype: 'storage' }),
  ...defs([['tool_rack', 'Tool Rack']], 'Interior', 'none', { surface: 'wall', ptype: 'furniture' }),
];

export const NATIVE_SUBCATEGORIES = ['Nature', 'Terrain', 'Foundations', 'Walls', 'Floors', 'Roofs', 'Infrastructure', 'Prefabs', 'Interior'];

// Sloped walkable pieces: an explicit single inclined collider replaces the
// default per-mesh cuboids (which turn stairs into un-climbable 0.3m steps and
// a ramp into a thin floating slab). Keyed by builderKey. low/high = [z, yTop]
// of the walking line's bottom and top ends; the collider spans ground→platform.
const SLOPE_COLLIDERS = {
  ramp:           { w: 1.6, low: [1.1, 0.0], high: [-1.1, 1.0], t: 0.25 },
  stair_straight: { w: 1.6, low: [1.0, 0.0], high: [-0.8, 1.5], t: 0.3 },
  stair_short:    { w: 1.6, low: [0.6, 0.0], high: [-0.6, 0.9], t: 0.3 },
  stair_wide:     { w: 2.56, low: [1.0, 0.0], high: [-0.8, 1.5], t: 0.3 },
  stair_stone:    { w: 1.8, low: [1.0, 0.0], high: [-0.8, 1.5], t: 0.3 },
  bridge_end:     { w: 1.8, low: [0.8, 0.0], high: [-0.8, 0.42], t: 0.25 },
  interior_step:  { w: 1.4, low: [0.5, 0.0], high: [-0.3, 0.5], t: 0.25 },
};
// Gate/entrance pieces: only the side posts collide so the central opening stays
// passable (the default per-mesh cuboids would seal any door-panel mesh shut).
const GATE_COLLIDERS = {
  property_gate_simple:  { half: 0.9, postW: 0.26, h: 1.8 },
  settlement_gate_heavy: { half: 1.0, postW: 0.4, h: 3.4 },
  mythic_gate:           { half: 1.0, postW: 0.4, h: 3.4 },
  farm_gate:             { half: 0.95, postW: 0.2, h: 1.4 },
  gate_posts:            { half: 1.0, postW: 0.26, h: 2.4 },
  carved_entrance_posts: { half: 1.0, postW: 0.4, h: 2.8 },
  palisade_gate:         { half: 0.95, postW: 0.6, h: 4.0 },
};

// id -> { build, collision, name, sub, realm, tier, snap, module, slope, gate }
export const NATIVE_ASSET_MAP = NATIVE_ASSETS.reduce((m, a) => {
  const slope = SLOPE_COLLIDERS[a.builderKey] || null;
  const gate = GATE_COLLIDERS[a.builderKey] || null;
  const collision = slope ? 'ramp' : gate ? 'gate' : a.collision;
  m[a.id] = { build: NATIVE_ASSET_BUILDERS[a.builderKey], collision, name: a.name, sub: a.sub, realm: a.realm, tier: a.tier, snap: a.snap, module: a.module, slope, gate };
  return m;
}, {});

// Grid/attachment snapping for structural pieces. Returns a snapped world
// transform so roughly-placed compatible pieces connect cleanly. Non-structural
// (nature/interior/prefabs) stay freeform (y=0, no grid).
export function snapTransform(assetId, x, z) {
  const meta = NATIVE_ASSET_MAP[assetId];
  const snap = meta && meta.snap;
  if (!snap) return { position: [x, 0, z], snapped: false, rotStep: null };
  const g = snap.grid;
  return { position: [Math.round(x / g) * g, snap.y || 0, Math.round(z / g) * g], snapped: true, rotStep: snap.rotStep };
}
